import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { dispatch, store } from 'store';
import { CompanyPlatformsModel } from 'types/company-platforms/companyPlatforms';
import { WebHookType } from 'types/company-platforms/webHooks';
import { fetcher } from 'utils/axios';
import { updateCompanyData } from './company';
import { isArray, mergeWith } from 'lodash-es';

const initialState: CompanyPlatformsModel = {
  fireberry: {
    companyInfo: {
      companyName: '',
      companyId: '',
      useMainTokenAll: true,
      mainTokenAsFallback: true,
    },
    contactsSettings: {
      updateWithFireberry: true,
      createNew: true,
      objectsFields: [],
    },
    clientsSettings: {
      updateWithFireberry: true,
      createNew: true,
      objectsFields: [],
      createTask: true,
    },
    additionalProfilesList: [],
  },
  priority: {
    enabled: true,
    //* Credentials
    token: '',
    domain: '',
    username: '',
    login: '',
    password: '',
    url: '',
    company: '',
    tabulaini: '',
    mainTokenAsFallback: true,
    useMainTokenAll: true,
    displayConversationByDefault: true,
    licenseType: '',
    version: '',
    isConnector: true,
    //* Contacts and clients settings
    clientsSettings: {
      updateWithPriority: true,
      createNew: true,
      objectsFields: [],
    },
    contactsSettings: {
      updateWithPriority: true,
      createNew: true,
      objectsFields: [],
    },
    //* Configurations
    configurations: {
      defaultTemplate: null,
      priorityPlatformLabels: {
        mainViewSingle: {
          contactPhoneLabel: '',
          contactNameLabel: '',
        },
        mainViewTable: {
          contactPhoneLabel: '',
          contactNameLabel: '',
        },
      },
      serviceNames: {
        priorityContacts: {
          phone: '',
        },
      },
      closeChatWhenFocusOut: true,
      preciseDocDetection: true,
      syncSelectedColumns: [
        {
          key: 'contactsPhoneNumber',
          label: 'Contacts Phone Number',
          listType: 'contacts',
          selected: null,
          required: true,
        },
        {
          key: 'contactsFullName',
          label: 'Contacts Full Name',
          listType: 'contacts',
          selected: null,
          required: true,
        },
        {
          key: 'contactsAccountName',
          label: 'Contacts Account Name',
          listType: 'contacts',
          selected: null,
          required: false,
        },
        {
          key: 'customersPhoneNumber',
          label: 'Customers Phone Number',
          listType: 'customers',
          selected: null,
          required: true,
        },
        {
          key: 'customersFullName',
          label: 'Customers Full Name',
          listType: 'customers',
          selected: null,
          required: true,
        },
        {
          key: 'customersAccountName',
          label: 'Customers Account Name',
          listType: 'customers',
          selected: null,
          required: false,
        },
      ],
    },
    companyInfo: null, // ?
    userInfo: null, // ?
    windowSettings: null, // ?
    massActions: [],
    formats: [],
    additionalProfilesList: [],
    useApiToken: false,
    apiToken: '',
    docSource: 'link',
    endpoint: '/wcf/wcf/Service.svc',
  },
  autoStartBot: {
    enabled: false,
    value: '',
  },
  usersOptions: [],
  companyAboutInfo: {
    businessName: '',
    number: '',
    address: '',
    city: '',
    email: '',
    websiteUrl: '',
    image: '',
    name: '',
  },
  fireberryObjectsFields: null,
  webHooks: [
    {
      WebhookIdentifier: WebHookType.LABEL_CHANGED,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.CONVERSATION_CASE,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.FORWARD_MESSAGE,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.FORWARD_MESSAGES_BUTTONS,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.SEND_TASK,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.SET_READ,
      WebhookUrl: '',
    },
    {
      WebhookIdentifier: WebHookType.SET_UNREAD,
      WebhookUrl: '',
    },
  ],
  conversationStatus: {
    autoClose: {
      afterXTime: true,
    },
    associateWithLastUser: {
      unassignedCalls: true,
      assignedCalls: true,
    },
    callsOpenedNotAssociated: {
      createContact: true,
      incomingOnly: true,
    },
  },
  webHooksActiveStates: {
    messageWebhook: false,
    chatUserWebhook: false,
    contactStatusChangeWebhook: false,
    incomingContactStatusChangeOpenWebhook: false,
    incomingContactStatusChangeCloseWebhook: false,
    chatUserContactChangeWebhook: false,
    labelChangedWebhook: false,
    conversationCaseWebhook: false,
    forwardMessagesWebhook: false,
    setReadWebhook: false,
    setUnreadWebhook: false,
  },
  workingSchedule: [],
  additionalLink: {
    enabled: false,
    url: '',
    icon: null,
  },
  additionalLinks: [],
  chatUsersCustomizationSettings: [],
  chatUsersCustomizationAdditionalSettings: {
    contactsMargin: false,
  },
  chatCustomizationSettings: [],
  templatesTagsList: [],
  predefinedTemplates: [],
  blackList: {
    enabled: false,
    list: [],
  },
  whiteList: {
    enabled: false,
    list: [],
  },
};

function mergeArrays(objValue: any, srcValue: any) {
  if (isArray(srcValue)) {
    return srcValue;
  }
}

const companyPlatformsSlice = createSlice({
  name: 'companyPlatforms',
  initialState,
  reducers: {
    updateCompanyPlatformsData(state, action: PayloadAction<Partial<CompanyPlatformsModel>>) {
      if (state) {
        mergeWith(state, action.payload, mergeArrays);
      }
    },
    resetCompanyPlatformsData() {
      return initialState;
    },
  },
});

export default companyPlatformsSlice.reducer;
export const { updateCompanyPlatformsData, resetCompanyPlatformsData } = companyPlatformsSlice.actions;

export function getCompanyPlatformsFromCompany() {
  try {
    const company = store.getState().company;
    if (company?.CompanyPlatforms) {
      const parsedCompanyPlatforms = JSON.parse(company.CompanyPlatforms);
      dispatch(updateCompanyPlatformsData(parsedCompanyPlatforms));
    }
  } catch (error) {
    console.warn('getCompanyPlatformsFromCompany error: ', error);
  }
}
export async function fetchUpdateCompanyPlatforms(
  params: Partial<CompanyPlatformsModel>,
  sendNotification: boolean = true,
) {
  try {
    const currentCompanyPlatformsData = store.getState().companyPlatforms;
    if (!currentCompanyPlatformsData) {
      console.warn('fetchUpdateCompanyPlatformsData: No company platforms data yet');
      return false;
    }

    const updatedCompanyPlatformsData = { ...currentCompanyPlatformsData, ...params };

    const data = await fetcher(
      'post',
      [
        '/api/services/setOwnerCompanyPlatforms',
        { data: { platformsJsonObj: JSON.stringify(updatedCompanyPlatformsData) } },
      ],
      'history',
    );
    if (data?.result) {
      dispatch(updateCompanyPlatformsData(params));
      dispatch(
        updateCompanyData({
          CompanyPlatforms: JSON.stringify(updatedCompanyPlatformsData),
        }),
      );
    }
    if (sendNotification) {
      //TODO Localization
      enqueueSnackbar(data?.result ? 'Success' : 'Error', {
        variant: data?.result ? 'success' : 'error',
      });
    }

    return !!data?.result;
  } catch (error) {
    console.warn('fetchUpdateCompanyData err: ', error);
    //TODO Localization
    if (sendNotification)
      enqueueSnackbar('Error', {
        variant: 'error',
      });

    return false;
  }
}
